import React, { Component } from 'react';
import 'react-dropdown/style.css';
import './App.scss';
import {
    ReactiveBase,
    CategorySearch,
    ReactiveList,
    MultiDropdownList,
    StateProvider,
    ReactiveComponent,
    SelectedFilters, 
    RangeSlider,
} from '@appbaseio/reactivesearch';
import {
    GeoDistanceSlider
} from '@appbaseio/reactivemaps';

window.React = React;
class App extends Component {
    constructor(props) {
        super(props);

        // Distance configuration
        this.maxDistance = window.location.hostname.endsWith('.dk') ? 200 : 500;

        // Language setup
        this.currentLang = document.getElementById('root-es')
            ? document.getElementsByTagName('html')[0].getAttribute('lang').substring(0, 2).toUpperCase()
            : 'SV';

        this.currentLangQuery = this.currentLang.toLowerCase();
        if (this.currentLang === 'NB') {
            this.currentLangQuery = 'no';
        }

        // Currency and locale defaults
        this.priceField = 'SEK';
        this.currencyFormat = 'kr/st';
        this.currencyLocale = 'sv-SE';
        this.currencyFraction = 0;

        // Configure currency based on language
        this.configureCurrencyByLanguage();

        // Product configuration
        this.priceFactor = document.getElementById('head')?.getAttribute('data-priceFactor') || 1;
        this.getTerms = document.getElementById('head')?.getAttribute('data-getTerms')?.split(",") ||
            ['Använd - i nyskick', 'Ny - oanvänd', 'Använd - i bra skick', 'Använd - i använt skick'];

        // Categories this customer's agreement does not cover, from
        // templates/head.php, as product_cat ids in every market — the indexed
        // ids are per-language. Empty for anyone unrestricted and for everyone
        // browsing logged out. Parsed rather than split alone, because an empty
        // attribute splits to [''].
        this.hideCategories = (document.getElementById('head')?.getAttribute('data-hideCategories') || '')
            .split(',')
            .map(id => parseInt(id, 10))
            .filter(id => !isNaN(id));

        // Initialize state with URL params
        const params = new URLSearchParams(window.location.search);
        this.state = {
            numberOfResults: 0,
            sortBy: {
                value: params.get('sortby') ?? 'post_timestamp',
                label: 'Produktnamn',
                order: params.get('order') ?? 'desc'
            },
            sortOrder: 'asc',
            total: 0,
            location: false,
            time: 0,
            language: 'SV',
            showFilters: false,
            showSort: false,
            showEmissionFilter: false,
            dimensionRanges: {
                height: { min: 0, max: 6000 },
                width: { min: 0, max: 6000 },
                length: { min: 0, max: 10000 }
            },
        };

        // Initialize language strings
        this.strings = this.getLanguageStrings();

        // User roles
        this.isAdministrator      = document.body.classList.contains('administrator');
        this.isCustomerInternal   = document.body.classList.contains('customer-internal');
        this.isInternalUser       = this.isAdministrator || this.isCustomerInternal;

        // Bind methods
        this.updateSorting = this.updateSorting.bind(this);
        this.toggleFilters = this.toggleFilters.bind(this);
        this.toggleEmission = this.toggleEmission.bind(this);
        this.showFilter = this.showFilter.bind(this);
        this.getLocation = this.getLocation.bind(this);
        this.showSort = this.showSort.bind(this);
        this.hideFilters = this.hideFilters.bind(this);
        this.resetAll = this.resetAll.bind(this);
        this.toggleSort = this.toggleSort.bind(this);
        this.updateNumberOfResults = this.updateNumberOfResults.bind(this);
        this.fetchDimensionRanges = this.fetchDimensionRanges.bind(this);
    }

    componentDidMount() {
        this.fetchDimensionRanges();
    }

    /**
     * The clauses every query shares: published, this market, the condition
     * grades this user may be shown, and — on a restricted agreement — nothing
     * from the categories it does not cover.
     */
    baseBoolQuery() {
        const bool = {
            must: [
                { term: { post_status: 'publish' } },
                { term: { lang: this.currentLangQuery } },
                { terms: { "condition.keyword": this.getTerms } }
            ]
        };

        if (this.hideCategories.length) {
            // The two category fields every document carries, one each and
            // per-language — which is why the attribute lists every market's id.
            bool.must_not = [
                { terms: { category_id: this.hideCategories } },
                { terms: { subcategory_id: this.hideCategories } }
            ];
        }

        return bool;
    }

    async fetchDimensionRanges() {
        try {
            const response = await fetch('https://cn-search.inputinterior.se/els/greenified_products/_search', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    size: 0,
                    query: {
                        bool: this.baseBoolQuery()
                    },
                    aggs: {
                        height_range: {
                            stats: { field: "dimension_size_hojd" }
                        },
                        width_range: {
                            stats: { field: "dimension_size_bredd" }
                        },
                        length_range: {
                            stats: { field: "dimension_size_langd" }
                        }
                    }
                })
            });

            const data = await response.json();
            
            if (data.aggregations) {
                const heightStats = data.aggregations.height_range;
                const widthStats = data.aggregations.width_range;
                const lengthStats = data.aggregations.length_range;

                this.setState({
                    dimensionRanges: {
                        height: {
                            min: Math.floor(heightStats.min || 0),
                            max: Math.ceil(heightStats.max || 6000)
                        },
                        width: {
                            min: Math.floor(widthStats.min || 0),
                            max: Math.ceil(widthStats.max || 6000)
                        },
                        length: {
                            min: Math.floor(lengthStats.min || 0),
                            max: Math.ceil(lengthStats.max || 10000)
                        }
                    },
                    dimensionsLoaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching dimension ranges:', error);
            // Keep default values if fetch fails
            this.setState({ dimensionsLoaded: true });
        }
    }

    configureCurrencyByLanguage() {
        switch (this.currentLang) {
            case 'EN':
                this.currencyLocale = 'en-US';
                this.currencyFraction = 2;
                this.currencyFormat = 'kr/pcs';
                this.priceField = 'SEK';
                break;
            case 'DA':
                this.currencyLocale = 'da-DK';
                this.currencyFraction = 0;
                this.currencyFormat = 'kr/stk';
                this.priceField = 'DKK';
                break;
            case 'NB':
                this.currencyLocale = 'nb-NO';
                this.currencyFraction = 0;
                this.currencyFormat = 'kr/stk';
                this.priceField = 'NOK';
                break;
            case 'FI':
                this.currencyLocale = 'fi-FI';
                this.currencyFraction = 2;
                this.currencyFormat = 'euroa/kpl';
                this.priceField = 'EUR';
                break;
            case 'SE':
                this.currencyLocale = 'se-SV';
                this.currencyFraction = 0;
                this.currencyFormat = 'kr/st';
                this.priceField = 'SEK';
                break;
        }
    }

    getLanguageStrings() {
        const strings = {
            'SV': {
                'packaging': 'Produkten är ouppackad.',
                'no-results-title': 'Din sökning gav ingen träff',
                'no-results-text': 'Kontrollera att du har stavat korrekt eller ange mindre specifika sökord.',
                'co2-prefix': 'Minimera mitt',
                'co2-suffix': ' avtryck',
                'location-info': 'Ange din plats samt justera nedan för att visa produkter i din närhet.',
                'qty': 'st',
                'products': 'produkter',
                'ads': 'annonser',
                'matches': 'Träffar',
                'unit': 'st',
                'search-label': 'Sök bland våra produkter',
                'search-short-label': 'Sök ...',
                'all-label': 'Alla',
                'condition-label': 'Skick',
                'brand-label': 'Varumärke',
                'color-label': 'Färg',
                'material-label': 'Material',
                'designer-label': 'Designer',
                'length-label': 'Längd',
                'width-label': 'Bredd/Djup',
                'height-label': 'Höjd',
                'category-label': 'Kategori',
                'subcategory-label': 'Underkategori',
                'enviroment-label': 'Miljömärkningar',
                'filter-label': 'Filter',
                'filter-classic': 'Rea',
                'sort-label': 'Sortering',
                'sort-standard': 'Standard',
                'sort-name': 'Produktnamn',
                'sort-new': 'Nyast',
                'sort-best': 'Bästa match',
                'sort-popular': 'Populära produkter',
                'sort-qty': 'Antal',
                'sort-date': 'Senast inlagda',
                'sort-price': 'Pris',
                'search-in-prefix': 'Sök efter',
                'search-category': 'Kategori',
                'search-in-suffix': 'i alla kategorier'
            },
            'EN': {
                'packaging': 'The product is unpackaged.',
                'no-results-title': 'Your search yielded no results',
                'no-results-text': 'Check that you have spelled correctly or enter less specific search terms.',
                'co2-prefix': 'Minimize my',
                'co2-suffix': ' footprint',
                'location-info': 'Enter your location and adjust below to show products near you.',
                'qty': 'pcs',
                'products': 'products',
                'ads': 'ads',
                'matches': 'Matches',
                'unit': 'pcs',
                'search-label': 'Search among our products',
                'search-short-label': 'Search ...',
                'all-label': 'All',
                'condition-label': 'Condition',
                'brand-label': 'Brand',
                'color-label': 'Color',
                'material-label': 'Material',
                'designer-label': 'Designer',
                'length-label': 'Length',
                'width-label': 'Width/Depth',
                'height-label': 'Height',
                'category-label': 'Category',
                'subcategory-label': 'Subcategory',
                'enviroment-label': 'Environmental certifications',
                'filter-label': 'Filter',
                'filter-classic': 'Sale',
                'sort-label': 'Sort',
                'sort-standard': 'Standard',
                'sort-name': 'Product name',
                'sort-new': 'Newest',
                'sort-best': 'Best match',
                'sort-popular': 'Popular products',
                'sort-qty': 'Quantity',
                'sort-date': 'Latest added',
                'sort-price': 'Price',
                'search-in-prefix': 'Search for',
                'search-category': 'Category',
                'search-in-suffix': 'in all categories'
            },
            'FI': {
                'packaging': 'Tuote on alkuperäispakkauksessa.',
                'no-results-title': 'Hakusi ei tuottanut osumia',
                'no-results-text': 'Varmista, että olet kirjoittanut oikein tai kirjoita vähemmän tarkkoja avainsanoja.',
                'co2-prefix': 'Minimoi',
                'co2-suffix': '-jalanjälkeni',
                'location-info': 'Anna sijaintisi ja säädä alla nähdäksesi lähelläsi olevat tuotteet..',
                'qty': 'kpl',
                'products': 'tuotteet',
                'ads': 'mainokset',
                'search-label': 'Etsi tuotteistamme',
                'search-short-label': 'Etsi',
                'all-label': 'Kaikki',
                'condition-label': 'Kunto',
                'brand-label': 'Tuotemerkki',
                'color-label': 'Väri',
                'material-label': 'Materiaali',
                'designer-label': 'Suunnittelija',
                'length-label': 'Piitus',
                'width-label': 'Leveys / Syvyys',
                'height-label': 'Korkeus',
                'category-label': 'Kategoria',
                'subcategory-label': 'Alakategoria',
                'enviroment-label': 'Ympäristömerkinnät',
                'filter-label': 'Hakuehdot',
                'filter-classic': 'Klassikot',
                'sort-label': 'Hakutulos',
                'sort-standard': 'Standardi',
                'sort-name': 'Tuotteen nimi',
                'sort-new': 'Uusimmat',
                'sort-best': 'Bästa match',
                'sort-popular': 'Suositut tuotteet',
                'sort-qty': 'Määrä',
                'sort-date': 'Viimeksi lisätyt',
                'sort-price': 'Hinta',
                'search-in-prefix': 'Etsi',
                'search-category': 'Kategoria',
                'search-in-suffix': 'kaikista kategorioista'
            },
            'NB': {
                'packaging': 'Produktet er i originalemballasje.',
                'no-results-title': 'Søket ditt ga ingen treff',
                'no-results-text': 'Kontroller at du har stavet riktig eller skriv inn mindre spesifikke søkeord.',
                'co2-prefix': 'Minimer mitt',
                'co2-suffix': '-avtrykk',
                'location-info': 'Skriv inn posisjonen din og juster nedenfor for å se produkter nær deg..',
                'qty': 'stk.',
                'products': 'produkter',
                'ads': 'annonser',
                'search-label': 'Søk blant våre produkter',
                'search-short-label': 'Søk',
                'all-label': 'Alle',
                'condition-label': 'Tilstand',
                'brand-label': 'Varemerke',
                'color-label': 'Farge',
                'material-label': 'Materiale',
                'designer-label': 'Designer',
                'length-label': 'Lengde',
                'width-label': 'Bredde / Dybde',
                'height-label': 'Høyde',
                'category-label': 'Kategori',
                'subcategory-label': 'Underkategori',
                'enviroment-label': 'Miljømerker',
                'filter-label': 'Filter',
                'filter-classic': 'Klassiker',
                'sort-label': 'Sortering',
                'sort-standard': 'Standard',
                'sort-name': 'Produktnavn',
                'sort-new': 'Nyeste',
                'sort-best': 'Beste treff',
                'sort-popular': 'Populære produkter',
                'sort-qty': 'Antall',
                'sort-date': 'Nyeste produkter',
                'sort-price': 'Pris',
                'search-in-prefix': 'Søk etter',
                'search-category': 'Kategori',
                'search-in-suffix': 'i alle kategorier'
            }

        };

        return strings[this.currentLang] || strings['SV'];
    }

    getHighlightField(value, object) {
        let key = Object.keys(object).find(key => object[key] === value);

        if (key === 'designer' || key === 'designer_poplist') {
            return this.strings['designer-label'];
        }
        if (key === 'varumarke') {
            return this.strings['brand-label'];
        }
        if (key === 'post_title') {
            return this.strings['sort-name'];
        }
        if (key === 'underkategori_ngram' || key === 'underkategori') {
            return this.strings['subcategory-label'];
        }
        if (key === 'huvudkategori') {
            return this.strings['category-label'];
        }
        return key;
    }

    updateSorting(newSort) {
        let params = new URLSearchParams(window.location.search);
        params.set('sortby', newSort.value);
        params.set('order', newSort.order);
        let newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${params.toString()}`;
        window.history.pushState({ path: newUrl }, '', newUrl);

        this.setState({
            sortBy: {
                value: newSort.value,
                order: newSort.order
            },
        });

        if (window.screen.width < 990) {
            this.hideFilters();
        }
    }

    toggleFilters() {
        this.setState({
            showFilters: !this.state.showFilters,
            showEmissionFilter: false,
            showSort: false,
        });
    }

    toggleEmission() {
        this.setState({
            showFilters: false,
            showEmissionFilter: !this.state.showEmissionFilter,
            showSort: false,
        });
    }

    showFilter() {
        this.setState({
            showFilters: true,
            showEmissionFilter: false,
            showSort: false,
        });
    }

    getLocation() {
        this.setState({ location: true });
    }

    showSort() {
        this.setState({
            showFilters: false,
            showEmissionFilter: false,
            showSort: true,
        });
    }

    hideFilters() {
        this.setState({
            showFilters: false,
            showEmissionFilter: false,
            showSort: false,
        });
    }

    resetAll() {
        window.location.href = 'https://greenified.se/produkter/';
    }

    toggleSort() {
        this.setState({
            showSort: !this.state.showSort,
            showEmissionFilter: false,
            showFilters: false,
        });
    }

    updateNumberOfResults(results) {
        this.setState({ numberOfResults: results });
    }

    renderDimensionSliders() {
        if (!this.state.dimensionsLoaded) {
            return <div>Loading dimension filters...</div>;
        }

        const { dimensionRanges } = this.state;

        return (
            <>
                <div className="filter">
                    <div className="filter__container half-width slider-container">
                        <RangeSlider
                            componentId="HeightSliderSensor"
                            dataField="dimension_size_hojd"
                            filterLabel={this.strings["height-label"]}
                            title={this.strings['height-label']}
                            range={{
                                start: dimensionRanges.height.min,
                                end: dimensionRanges.height.max,
                            }}
                            rangeLabels={{
                                start: dimensionRanges.height.min + ' MM',
                                end: dimensionRanges.height.max + ' MM',
                            }}
                            tooltipTrigger="hover"
                            renderTooltipData={data => (
                                <div style={{
                                    fontSize: '10px',
                                    whiteSpace: 'nowrap',                              
                                }}>
                                    {data} MM
                                </div>
                            )}
                            showHistogram={false}
                            showFilter={true}
                            snap={false}
                            stepValue={10}
                            URLParams={true}
                            react={{
                                and: ['SearchSensor', 'categoryFilter', 'subCategoryFilter', 'makeFilter',
                                    'materialFilter', 'colorFilter', 'brandFilter', 'conditionFilter',
                                    'enviromentFilter', 'languageFilter', 'LengthSliderSensor', 'WidthSliderSensor']
                            }}
                        />
                    </div>
                </div>
                <div className="filter">
                    <div className="filter__container half-width slider-container">
                        <RangeSlider
                            componentId="WidthSliderSensor"
                            dataField="dimension_size_bredd"
                            filterLabel={this.strings["width-label"]}
                            title={this.strings['width-label']}
                            range={{
                                start: dimensionRanges.width.min,
                                end: dimensionRanges.width.max,
                            }}
                            rangeLabels={{
                                start: dimensionRanges.width.min + ' MM',
                                end: dimensionRanges.width.max + ' MM',
                            }}
                            tooltipTrigger="hover"
                            renderTooltipData={data => (
                                <div style={{
                                    fontSize: '10px',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {data} MM
                                </div>
                            )}
                            showHistogram={false}
                            showFilter={true}
                            snap={false}
                            stepValue={10}
                            URLParams={true}
                            react={{
                                and: ['SearchSensor', 'categoryFilter', 'subCategoryFilter', 'makeFilter',
                                    'materialFilter', 'colorFilter', 'brandFilter', 'conditionFilter',
                                    'enviromentFilter', 'languageFilter', 'HeightSliderSensor', 'LengthSliderSensor']
                            }}
                        />
                    </div>
                </div>
                <div className="filter">
                    <div className="filter__container half-width slider-container">
                        <RangeSlider
                            componentId="LengthSliderSensor"
                            dataField="dimension_size_langd"
                            filterLabel={this.strings["length-label"]}
                            title={this.strings['length-label']}
                            range={{
                                start: dimensionRanges.length.min,
                                end: dimensionRanges.length.max,
                            }}
                            rangeLabels={{
                                start: dimensionRanges.length.min + ' MM',
                                end: dimensionRanges.length.max + ' MM',
                            }}
                            tooltipTrigger="hover"
                            renderTooltipData={data => (
                                <div style={{
                                    fontSize: '10px',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {data} MM
                                </div>
                            )}
                            showHistogram={false}
                            showFilter={true}
                            snap={false}
                            stepValue={10}
                            URLParams={true}
                            react={{
                                and: ['SearchSensor', 'categoryFilter', 'subCategoryFilter', 'makeFilter',
                                    'materialFilter', 'colorFilter', 'brandFilter', 'conditionFilter',
                                    'enviromentFilter', 'languageFilter', 'HeightSliderSensor', 'WidthSliderSensor']
                            }}
                        />
                    </div>
                </div>
            </>
        );
    }

    render() {
        return (
            <ReactiveBase
                mapLibraries={['places']}
                app="greenified_products"
                url="https://cn-search.inputinterior.se/els/"
                mapKey="AIzaSyDX4B105jDOh1Z4eKppFYpIqtWcbXVUGV4"
            >
                <ReactiveComponent
                    componentId="languageFilter"
                    customQuery={props => ({
                        aggs: {
                            stock: {
                                sum: { field: "qty" }
                            },
                        },
                        query: {
                            bool: this.baseBoolQuery()
                        },
                    })}
                />
                <div className="wrap container active reactivesearch" role="document">
                    <div className="content row">
                        <main className="main">
                            <div id="algoliaheader"
                                style={{
                                    backgroundColor: "#f7f7f7",
                                    backgroundImage: "url(https://greenified.se/wp-content/uploads/2021/03/search-header.jpg)",
                                }}>
                                <div className="aspectholder">
                                    <div className="searcharea">
                                        <div className="searcharea-input">
                                            <CategorySearch
                                                componentId="SearchSensor"
                                                filterLabel={this.strings['search-short-label']}
                                                showClear={true}
                                                showDistinctSuggestions={false}
                                                dataField={['post_title', 'huvudkategori', 'underkategori', 'designer_poplist', 'varumarke']}
                                                categoryField="underkategori.keyword"
                                                showIcon={false}
                                                URLParams={true}
                                                fieldWeights={[1, 3, 3, 5, 10, 10]}
                                                fuzziness={0}
                                                placeholder={this.strings["search-label"]}
                                                react={{
                                                    and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                        'categoryFilter',
                                                        'subCategoryFilter',
                                                        'makeFilter',
                                                        'LocationUI',
                                                        'materialFilter',
                                                        'colorFilter',
                                                        'brandFilter',
                                                        'conditionFilter',
                                                        'enviromentFilter',
                                                        'HeightSliderSensor', 'WidthSliderSensor', 'LengthSliderSensor']
                                                }}
                                                render={({
                                                    loading,
                                                    error,
                                                    value,
                                                    categories,
                                                    suggestions,
                                                    aggregationData,
                                                    downshiftProps: { isOpen, getItemProps, highlightedIndex },
                                                }) => {
                                                    if (loading) return null;
                                                    if (error) {
                                                        return <div>Something went wrong! Error
                                                            details {JSON.stringify(error)}</div>;
                                                    }
                                                    return isOpen && Boolean(value.length) ? (
                                                        <div className="search-suggestions">
                                                            {Boolean(value.length) && (
                                                                <div
                                                                    className={0 === highlightedIndex ? 'highlighted' : null}>
                                                                    <div {...getItemProps({
                                                                        item: {
                                                                            label: value,
                                                                            value: value
                                                                        }
                                                                    })}>
                                                                        {this.strings['search-in-prefix']}
                                                                        <strong>{value}</strong> {this.strings['search-in-suffix']}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {suggestions.slice(0, 6).map((suggestion, indexfirst) => (
                                                                <div
                                                                    className={(indexfirst + 1) === highlightedIndex ? 'highlighted' : null}
                                                                    key={suggestion.value}
                                                                    {...getItemProps({ item: suggestion })}>
                                                                    <span className="spacer">
                                                                        <span>{this.getHighlightField(suggestion.value, suggestion.source)}</span>
                                                                    </span>
                                                                    {suggestion.value}
                                                                </div>
                                                            ))}
                                                            {categories.slice(0, 3).map((category, index) => (
                                                                <div
                                                                    className={((index + 1) + suggestions.slice(0, 6).length) === highlightedIndex ? 'highlighted category-view' : 'category-view'}
                                                                    key={category.key}
                                                                    {...getItemProps({
                                                                        item: {
                                                                            value: value,
                                                                            category: category.key
                                                                        }
                                                                    })}>
                                                                    {value} i <strong>{category.key}</strong>
                                                                    <span className="spacer">
                                                                        <span>{this.strings['search-category']}</span>
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : null;
                                                }}
                                            />
                                        </div>
                                        <div className="clearFilters">
                                            <SelectedFilters showClearAll={false} />
                                        </div>
                                        <div className="launchfilter">
                                            <span onClick={this.toggleFilters}>Filtrera</span> /
                                            <span onClick={this.toggleSort}>Sortera</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                    <div className="module filter-toggle">
                        <div className="modulerow">
                            <div className="modulecol col-1-2">
                                <StateProvider
                                    render={({ searchState }) => (
                                        <h3>
                                            {searchState.SearchResult?.aggregations?.stock?.value} {this.strings['products']} -
                                            {searchState.SearchResult?.hits?.total} {this.strings['ads']}
                                        </h3>
                                    )}
                                />
                            </div>
                            <div className="modulecol col-1-2 filter-toggle">
                                <div role="button" className="cursor-pointer" onClick={this.toggleFilters}>
                                    {this.strings['filter-label']}
                                    <img
                                        src="https://cdn0.iconfinder.com/data/icons/google-material-design-icons/48/ic_filter_list_48px-512.png"
                                        height="30" />
                                </div>
                                <div role="button" className="cursor-pointer" onClick={this.toggleSort}>
                                    {this.strings['sort-label']}
                                    <img
                                        src="https://cdn0.iconfinder.com/data/icons/google-material-design-3-0/48/ic_swap_vert_48px-512.png" />
                                </div>
                                <div role="button" className="cursor-pointer" onClick={this.toggleEmission}>
                                    {this.strings['co2-prefix']} CO<sub>2</sub>{this.strings['co2-suffix']}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="module algoliafilter" data-scroll-scope=""
                        style={{ display: this.state.showSort ? "block" : "none" }}>
                        <div className="modulerow">
                            <div className="modulecol" data-emergence="visible">
                                <div className="algoliafilters">
                                    <div className="filtertabs">
                                        <div className="filtertabs__tab filtertabs__tab--active"
                                            data-group="filtergroup--sorting"
                                            onClick={this.showSort}>
                                            Sortering
                                        </div>
                                        <div className="filtertabs__tab"
                                            data-group="filtergroup--filtering"
                                            onClick={this.showFilter}>
                                            Filtrering
                                        </div>
                                    </div>
                                    <div className="filtergroup filtergroup--sorting filtergroup--visible">
                                        <div className="filter">
                                            <div className="filter__container half-width">
                                                <button onClick={() => this.updateSorting({
                                                    value: 'views_total',
                                                    label: 'Bästa match',
                                                    order: 'desc'
                                                })}>
                                                    {this.strings['sort-popular']}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="filter"
                                            style={{ display: this.state.sortBy.order === 'asc' ? "block" : "none" }}>
                                            <div className="filter__container half-width">
                                                <button onClick={() => this.updateSorting({
                                                    value: 'qty',
                                                    label: 'Bästa match',
                                                    order: 'desc'
                                                })}>
                                                    {this.strings['sort-qty']} &darr;
                                                </button>
                                            </div>
                                        </div>
                                        <div className="filter"
                                            style={{ display: this.state.sortBy.order === 'desc' ? "block" : "none" }}>
                                            <div className="filter__container half-width">
                                                <button onClick={() => this.updateSorting({
                                                    value: 'qty',
                                                    label: 'Bästa match',
                                                    order: 'asc'
                                                })}>
                                                    {this.strings['sort-qty']} &uarr;
                                                </button>
                                            </div>
                                        </div>
                                        <div className="filter">
                                            <div className="filter__container half-width">
                                                <button onClick={() => this.updateSorting({
                                                    value: 'post_timestamp',
                                                    label: 'Senast inlagda',
                                                    order: 'desc'
                                                })}>
                                                    {this.strings['sort-date']}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="filter"
                                            style={{ display: this.state.sortBy.order === 'desc' ? "block" : "none" }}>
                                            <div className="filter__container half-width">
                                                <button onClick={() => this.updateSorting({
                                                    value: 'price_raw',
                                                    label: 'Pris stigande',
                                                    order: 'asc'
                                                })}>
                                                    {this.strings['sort-price']} &uarr;
                                                </button>
                                            </div>
                                        </div>
                                        <div className="filter"
                                            style={{ display: this.state.sortBy.order === 'asc' ? "block" : "none" }}>
                                            <div className="filter__container half-width">
                                                <button onClick={() => this.updateSorting({
                                                    value: 'price_raw',
                                                    label: 'Pris fallande',
                                                    order: 'desc'
                                                })}>
                                                    {this.strings['sort-price']} &darr;
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="filterbuttons">
                                        <div className="filterbuttons--buttoncol">
                                            <div className="btn btn--small black search-reset"
                                                onClick={this.resetAll}>Återställ
                                            </div>
                                        </div>
                                        <div className="filterbuttons--buttoncol">
                                            <div className="btn btn--small btn--search" onClick={this.hideFilters}>Sök
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="module algoliafilter" data-scroll-scope=""
                        style={{ display: this.state.showFilters ? "block" : "none" }}>
                        <div className="mobileclose search-close"></div>
                        <div className="modulerow">
                            <div className="modulecol" data-emergence="visible">
                                <div className="algoliafilters">
                                    <div className="filtertabs">
                                        <div className="filtertabs__tab"
                                            data-group="filtergroup--sorting"
                                            onClick={this.showSort}>Sortering
                                        </div>
                                        <div className="filtertabs__tab filtertabs__tab--active"
                                            data-group="filtergroup--filtering"
                                            onClick={this.showFilter}>Filtrering
                                        </div>
                                    </div>
                                    <div className="filtergroup filtergroup--filtering filtergroup--visible">
                                        <div className="filter">
                                            <div className="filter__container half-width">

                                                <MultiDropdownList
                                                    searchPlaceholder={this.strings['search-short-label']}
                                                    componentId="categoryFilter"
                                                    dataField="huvudkategori.keyword"
                                                    placeholder={this.strings['category-label']}
                                                    showSearch={false}
                                                    sortBy="asc"
                                                    showCount={false}
                                                    size={100}
                                                    URLParams={true}
                                                    showFilter={true}
                                                    filterLabel={this.strings["category-label"]}
                                                    react={{
                                                        and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                            'SearchSensor', 'subCategoryFilter', 'makeFilter',
                                                            'materialFilter', 'colorFilter', 'brandFilter',
                                                            'conditionFilter', 'enviromentFilter']
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="filter">
                                            <div className="filter__container half-width">
                                                <MultiDropdownList
                                                    searchPlaceholder={this.strings['search-short-label']}
                                                    componentId="subCategoryFilter"
                                                    dataField="underkategori.keyword"
                                                    placeholder={this.strings['subcategory-label']}
                                                    showSearch={false}
                                                    sortBy="asc"
                                                    showCount={false}
                                                    size={100}
                                                    URLParams={true}
                                                    showFilter={true}
                                                    filterLabel={this.strings['subcategory-label']}
                                                    react={{
                                                        and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                            'SearchSensor', 'categoryFilter', 'makeFilter',
                                                            'materialFilter', 'colorFilter', 'brandFilter',
                                                            'conditionFilter', 'enviromentFilter']
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="filter">
                                            <div className="filter__container">
                                                <MultiDropdownList
                                                    searchPlaceholder={this.strings['search-short-label']}
                                                    componentId="brandFilter"
                                                    dataField="varumarke.keyword"
                                                    placeholder={this.strings['brand-label']}
                                                    sortBy="asc"
                                                    showSearch={true}
                                                    URLParams={true}
                                                    showCount={false}
                                                    size={600}
                                                    showFilter={true}
                                                    filterLabel={this.strings["brand-label"]}
                                                    react={{
                                                        and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                            'SearchSensor', 'categoryFilter', 'subCategoryFilter',
                                                            'makeFilter', 'materialFilter', 'colorFilter',
                                                            'conditionFilter', 'enviromentFilter']
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="filter">
                                            <div className="filter__container half-width">
                                                <MultiDropdownList
                                                    searchPlaceholder={this.strings['search-short-label']}
                                                    componentId="conditionFilter"
                                                    dataField="condition.keyword"
                                                    filterLabel={this.strings["condition-label"]}
                                                    sortBy="asc"
                                                    showSearch={false}
                                                    URLParams={true}
                                                    showCount={false}
                                                    size={400}
                                                    showFilter={true}
                                                    placeholder={this.strings["condition-label"]}
                                                    react={{
                                                        and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                            'SearchSensor', 'categoryFilter', 'subCategoryFilter',
                                                            'makeFilter', 'materialFilter', 'colorFilter',
                                                            'brandFilter', 'enviromentFilter']
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="filtergroup filtergroup--filtering">
                                        <div className="filter">
                                            <div className="filter__container half-width">
                                                <MultiDropdownList
                                                    searchPlaceholder={this.strings['search-short-label']}
                                                    componentId="makeFilter"
                                                    dataField="prop_material.label.keyword"
                                                    placeholder={this.strings["material-label"]}
                                                    sortBy="asc"
                                                    showSearch={false}
                                                    URLParams={true}
                                                    showCount={false}
                                                    size={150}
                                                    showFilter={true}
                                                    filterLabel={this.strings["material-label"]}
                                                    react={{
                                                        and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                            'SearchSensor', 'categoryFilter', 'subCategoryFilter',
                                                            'materialFilter', 'colorFilter', 'brandFilter',
                                                            'conditionFilter', 'enviromentFilter']
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="filter">
                                            <div className="filter__container half-width">
                                                <MultiDropdownList
                                                    searchPlaceholder={this.strings['search-short-label']}
                                                    componentId="colorFilter"
                                                    dataField="prop_farg.label.keyword"
                                                    placeholder={this.strings['color-label']}
                                                    sortBy="asc"
                                                    showSearch={true}
                                                    URLParams={true}
                                                    showCount={false}
                                                    size={150}
                                                    showFilter={true}
                                                    filterLabel={this.strings["color-label"]}
                                                    react={{
                                                        and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                            'SearchSensor', 'categoryFilter', 'subCategoryFilter',
                                                            'makeFilter', 'materialFilter', 'brandFilter',
                                                            'conditionFilter', 'enviromentFilter']
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="filter">
                                            <div className="filter__container">
                                                <MultiDropdownList
                                                    searchPlaceholder={this.strings['search-short-label']}
                                                    componentId="materialFilter"
                                                    dataField="designer_poplist.keyword"
                                                    placeholder={this.strings['designer-label']}
                                                    sortBy="asc"
                                                    showSearch={true}
                                                    URLParams={true}
                                                    showCount={false}
                                                    size={400}
                                                    showFilter={true}
                                                    filterLabel="Designer"
                                                    react={{
                                                        and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                            'SearchSensor', 'categoryFilter', 'subCategoryFilter',
                                                            'makeFilter', 'colorFilter', 'brandFilter',
                                                            'conditionFilter', 'enviromentFilter']
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="filter">
                                            <div className="filter__container half-width">
                                                <MultiDropdownList
                                                    componentId="enviromentFilter"
                                                    dataField="enviroment.label.keyword"
                                                    placeholder={this.strings['enviroment-label']}
                                                    sortBy="asc"
                                                    showSearch={false}
                                                    showCount={false}
                                                    size={150}
                                                    URLParams={true}
                                                    showFilter={true}
                                                    filterLabel={this.strings["enviroment-label"]}
                                                    react={{
                                                        and: ['languageFilter', 'areaFilter', 'salesFilter',
                                                            'SearchSensor', 'categoryFilter', 'subCategoryFilter',
                                                            'makeFilter', 'materialFilter', 'colorFilter',
                                                            'brandFilter', 'conditionFilter']
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="filtergroup filtergroup--filtering filtergroup--visible">
                                        {this.renderDimensionSliders()}
                                    </div>

                                    {this.isInternalUser && (
                                        <div className="filtergroup filtergroup--filtering only-internal">
                                            <div className="filter">
                                                <div className="filter__container half-width">
                                                    
                                                    {this.isCustomerInternal && (
                                                        <ReactiveComponent
                                                            componentId="internalTypeFilter"
                                                            filter={false}
                                                            customQuery={() => ({
                                                            query: { term: { "salesperson_type.keyword": "internal" } }
                                                            })}
                                                        />
                                                    )}

                                                    <MultiDropdownList
                                                        searchPlaceholder={this.strings['search-short-label']}
                                                        componentId="salesFilter"
                                                        dataField="salesperson.keyword"
                                                        placeholder="Säljare"
                                                        sortBy="asc"
                                                        showSearch={false}
                                                        URLParams={true}
                                                        showCount={false}
                                                        size={250}
                                                        showFilter={true}
                                                        filterLabel="Säljare"
                                                        react={{
                                                            and: ['languageFilter', 'areaFilter', ...(this.isCustomerInternal ? ['internalTypeFilter'] : []),
                                                                'SearchSensor', 'categoryFilter', 'subCategoryFilter',
                                                                'materialFilter', 'colorFilter', 'brandFilter',
                                                                'conditionFilter', 'enviromentFilter']
                                                        }}                                                    
                                                    />
                                                </div>
                                                  
                                            </div>
                                            {this.isAdministrator && (
                                                <div className="filter">
                                                    <div className="filter__container half-width">
                                                        <MultiDropdownList
                                                            searchPlaceholder={this.strings['search-short-label']}
                                                            componentId="areaFilter"
                                                            dataField="area.keyword"
                                                            placeholder="Ort"
                                                            sortBy="asc"
                                                            showSearch={true}
                                                            URLParams={true}
                                                            showCount={false}
                                                            size={150}
                                                            showFilter={true}
                                                            filterLabel="Ort"
                                                            react={{
                                                                and: ['languageFilter', 'salesFilter',
                                                                    'SearchSensor', 'categoryFilter', 'subCategoryFilter',
                                                                    'makeFilter', 'materialFilter', 'brandFilter',
                                                                    'conditionFilter', 'enviromentFilter']
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="filterbuttons">
                                        <div className="filterbuttons--buttoncol">
                                            <div className="btn btn--small black search-reset"
                                                onClick={this.resetAll}>Återställ
                                            </div>
                                        </div>
                                        <div className="filterbuttons--buttoncol">
                                            <div className="btn btn--small btn--search"
                                                onClick={this.hideFilters}>Sök
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Emission Filter Section */}
                    <div className="module algoliafilter" data-scroll-scope=""
                        style={{ display: this.state.showEmissionFilter ? "block" : "none" }}>
                        <div className="mobileclose search-close"></div>
                        <div className="modulerow">
                            <div className="modulecol" data-emergence="visible">
                                <div className="algoliafilters">
                                    <div className="filtertabs">
                                        <div className="filtertabs__tab" data-group="filtergroup--sorting"
                                            onClick={this.showSort}>Sortering
                                        </div>
                                        <div className="filtertabs__tab filtertabs__tab--active"
                                            data-group="filtergroup--filtering" onClick={this.showFilter}>Filtrering
                                        </div>
                                    </div>
                                    <div className="filtergroup filtergroup--filtering">
                                        <div className="filter filter-full">
                                            <div className="filter__container slider-container" style={{ position: 'relative' }}>
                                                <GeoDistanceSlider
                                                    componentId="LocationUI"
                                                    dataField="location"
                                                    filterLabel={'CO2' + this.strings['co2-suffix']}
                                                    tooltipTrigger="always"
                                                    countries={["dk", "se", "no", "fi"]}
                                                    range={{
                                                        start: 100,
                                                        end: this.maxDistance,
                                                    }}
                                                    rangeLabels={{
                                                        start: 'Max 100 km',
                                                        end: `Max ${this.maxDistance} km`,
                                                    }}
                                                    defaultValue={{
                                                        distance: this.maxDistance,
                                                    }}
                                                    placeholder={this.strings['location-info']}
                                                    unit="km"
                                                    autoLocation={this.state.location}
                                                    showFilter={true}
                                                    URLParams={false}
                                                    renderTooltipData={data => (
                                                        <h5 style={{
                                                            padding: '3px 0',
                                                            margin: '0',
                                                            width: '50px',
                                                            fontSize: '12px'
                                                        }}>
                                                            {data} km
                                                        </h5>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="filterbuttons">
                                        <div className="filterbuttons--buttoncol">
                                            <div className="btn btn--small black search-reset"
                                                onClick={this.resetAll}>Återställ
                                            </div>
                                        </div>
                                        <div className="filterbuttons--buttoncol">
                                            <div className="btn btn--small btn--search" onClick={this.hideFilters}>Sök
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ReactiveList
                        dataField={this.state.sortBy.value}
                        componentId="SearchResult"
                        pagination={true}
                        size={30}
                        pages={10}
                        onResultStats={(total, time) => {
                            if (this.state.total !== total) {
                                this.setState({ total, time })
                            }
                            return null;
                        }}
                        URLParams={true}
                        scrollOnChange={false}
                        showResultStats={false}
                        onData={(data) => {
                            this.updateNumberOfResults(data.resultStats.numberOfResults);
                        }}
                        onPageClick={() => {
                            window.scrollTo({
                                top: 450,
                                left: 0,
                                behavior: 'smooth'
                            });
                        }}
                        customQuery={this.customQuery}
                        defaultQuery={() => ({
                            sort: [{ [this.state.sortBy.value]: { order: this.state.sortBy.order } }]
                        })}
                        react={{
                            and: ['enviromentFilter', 'LocationUI', 'SearchSensor', 'conditionFilter', 'makeFilter',
                                'subCategoryFilter', 'materialFilter', 'categoryFilter',
                                'HeightSliderSensor', 'LengthSliderSensor', 'WidthSliderSensor', 'languageFilter',
                                'areaFilter', 'salesFilter', 'brandFilter', 'colorFilter'],
                        }}
                        renderNoResults={() => (
                            <div className="no-result">
                                <div>
                                    <h2>{this.strings['no-results-title']}</h2>
                                    <p>&nbsp;</p>
                                    <p>{this.strings['no-results-text']}</p>
                                </div>
                            </div>
                        )}
                        render={({ loading, error, data }) => (
                            <div className="module algoliaresults">
                                <div className="modulerow">
                                    {data.map(res => (
                                        <div className={`modulecol col-1-5 searchhit ${res.qty === 0 ? 'no-qty' : ''}`} key={res.ID}>
                                            <a href={res.product_url}>
                                                <div className="resultitem" data-id={res.ID} data-objectid={res.objectid}>
                                                    <div className="resultitem_content">
                                                        {res.labels === 'packaging' && (
                                                            <div className="info-banner">{this.strings['packaging']}</div>
                                                        )}
                                                        {res.labels === 'discount' && (
                                                            <div className="info-banner">{res.discount_info[this.currentLang.toLowerCase()]}</div>
                                                        )}
                                                        {res.qty === 0 && (
                                                            <div className="info-banner">{res.stock_info}</div>
                                                        )}

                                                        <div className="resultitem_content--image"
                                                            style={{ backgroundImage: `url(${res.main_image})` }}>
                                                        </div>
                                                        {res.labels === 'packaging' && (
                                                            <img className="label-icon" src={require('./packaging.png')} />
                                                        )}
                                                        {res.labels === 'discount' && (
                                                            <img className="label-icon" src={require('./discount.png')} />
                                                        )}
                                                        {res.qty === 0 && (
                                                            <img className="label-icon" src={require('./reserved.png')} />
                                                        )}

                                                        <div className="resultitem_content--info">
                                                            <div style={{ display: 'flex' }}>
                                                                <h4 style={{ flex: 1 }}>
                                                                    {res.post_title}
                                                                    <span className="thin-font">
                                                                        {res.qty} {this.strings['qty']}
                                                                    </span>
                                                                </h4>
                                                            </div>
                                                            <div>
                                                                <p>
                                                                    {(() => {
                                                                        // Use unrounded_prices for EUR if available, otherwise fallback to prices
                                                                        const useUnroundedPrice = this.priceField === 'EUR' && res.unrounded_prices && res.unrounded_prices[this.priceField] != null;
                                                                        const price = useUnroundedPrice
                                                                            ? parseFloat(res.unrounded_prices[this.priceField])
                                                                            : res.prices[this.priceField];

                                                                        const finalPrice = useUnroundedPrice
                                                                            ? price * this.priceFactor
                                                                            : Math.round(price * this.priceFactor);

                                                                        return finalPrice.toLocaleString(this.currencyLocale, {
                                                                            minimumFractionDigits: useUnroundedPrice ? this.currencyFraction : 0,
                                                                            maximumFractionDigits: useUnroundedPrice ? this.currencyFraction : 0
                                                                        }) + " " + this.currencyFormat;
                                                                    })()}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    />
                </div>
            </ReactiveBase>
        );
    }
}


export default App;